import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Globe } from 'lucide-react';
import { companyInfo } from '../data/mock';
import BrandLogo from './BrandLogo';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Challenges', href: '#challenges' },
    { label: 'Solutions', href: '#solutions' },
    { label: 'Process', href: '#process' },
    { label: 'AI Integration', href: '#ai' },
    { label: 'Services', href: '#services' },
    { label: 'Workshops & Training', href: '/workshops', isExternal: true },
    { label: 'Upcoming Events', href: '/events', isExternal: true },
    { label: 'Case Studies', href: '/case-studies', isExternal: true },
    { label: 'Find Your DISC Bird', href: '/find-your-bird', isExternal: true },
    { label: 'Free Assessment', href: '/assessment', isExternal: true },
    { label: 'Consultation', href: '#consultation' }
  ];

  const services = [
    'Leadership Development',
    'Workforce Development',
    'Professional Development',
    'AI Integration & Training',
    'Assessments & Coaching',
    'Custom Programs'
  ];

  const scrollToSection = (href, isExternal = false) => {
    if (isExternal) {
      navigate(href);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-12 sm:py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <BrandLogo
              className="h-20 w-auto mb-4"
              fallbackClassName="text-xl font-bold tracking-wide text-white whitespace-nowrap block mb-4"
              testId="footer-logo-img"
            />
            <p className="text-slate-400 text-sm mb-4 leading-relaxed">
              Workforce, leadership, and AI solutions for modern organizations.
            </p>
            <a
              href={`https://${companyInfo.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm"
            >
              <Globe className="h-4 w-4" />
              {companyInfo.website}
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollToSection(link.href, link.isExternal)}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {services.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Get in Touch</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:info@thewementality.com"
                  className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                >
                  <Mail className="h-4 w-4" />
                  info@thewementality.com
                </a>
              </li>
            </ul>
            <div className="mt-6 p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <p className="text-slate-400 text-xs">
                Serving businesses, schools, agencies, and organizations nationwide.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm text-center sm:text-left">
            © {currentYear} {companyInfo.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
